import { action, computed, observable, runInAction } from 'mobx'
import type {
  GetCurrentUserWorkspaceMembershipsFn,
  GetWorkspaceSkillsFn,
  ListSkillsFn,
  UpdateMemberSkillsFn,
} from '@/features/iam'
import { task } from 'mobx-task'
import { func, validationContext } from 'validx'
import type { AlertFactory, AlertRetryWrapperFactory } from '@/features/shell'
import { type SearchableUser } from '@/features/iam/users/SearchableUser'
import { type Skill } from '@/features/iam/skills/Skill'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type Id } from '@/infra/id'
import { pluralize } from '@/utils/pluralization-util'

interface SkillManagementResult {
  user: SearchableUser
  success?: boolean
  error?: Error
}

export type ManageSkillsDialogTab = 'Add' | 'Remove'

export class ManageSkillsDialogViewModel {
  readonly modal = new ModalViewModel()

  /**
   * The currently selected tab.
   */
  @observable
  accessor selectedTab: ManageSkillsDialogTab = 'Add'

  /**
   * Whether to use the passed-in user as the dialog target.
   */
  @observable
  accessor selectedUser: SearchableUser | null = null

  /**
   * The list of available skills.
   */
  @observable
  accessor availableSkills: Skill[] = []

  /**
   * The currently selected workspace.
   */
  @observable
  accessor workspace: Workspace | null = null

  /**
   * The currently selected skills.
   */
  @observable
  accessor skills: Skill[] = []

  /**
   * Validation rules for managing skills.
   */
  validation = validationContext(this, {
    workspace: [
      func(() => this.workspace !== null, 'You must select a workspace.'),
    ],
    skills: [
      func(() => this.skills.length > 0, 'You must select at least one skill.'),
    ],
  })

  constructor(
    private readonly _selectedWorkspace: () => Workspace,
    private readonly _selectedUserIds: () => Set<Id>,
    private readonly _users: () => readonly SearchableUser[],
    private readonly _toggleUser: (checked: boolean, userId: Id) => void,
    private readonly _listSkills: ListSkillsFn,
    private readonly _getWorkspaceSkills: GetWorkspaceSkillsFn,
    private readonly _addMemberSkills: UpdateMemberSkillsFn,
    private readonly _removeMemberSkills: UpdateMemberSkillsFn,
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {}

  /**
   * The currently selected workspace.
   */
  @computed
  get selectedWorkspace(): Workspace | null {
    return this._selectedWorkspace() === Workspace.none
      ? null
      : this._selectedWorkspace()
  }

  /**
   * The name of the contextual user, if any.
   */
  @computed
  get selectedUserName() {
    return this.selectedUser ? this.selectedUser.displayName : null
  }

  /**
   * The name of the workspace chosen on the page, if any.
   */
  @computed
  get selectedWorkspaceName() {
    return this.selectedWorkspace ? this.selectedWorkspace.name : null
  }

  /**
   * The currently selected users.
   */
  @computed
  get selectedUsers() {
    return this._users().filter((u) => this._selectedUserIds().has(u.publicId))
  }

  /**
   * The list of workspaces available to the user.
   */
  @computed
  get workspaces() {
    return this._getCurrentUserWorkspaceMemberships()
      .filter((mb) => mb.member.permissions.adminUserList)
      .map((mb) => mb.workspace)
  }

  /**
   * Untoggle a user.
   */
  @action.bound
  untoggleUser(userId: Id) {
    this._toggleUser(false, userId)
  }

  /**
   * Set the selected tab.
   */
  @action.bound
  setSelectedTab(tab: ManageSkillsDialogTab) {
    if (tab === this.selectedTab) {
      return
    }

    this.selectedTab = tab
    if (this.workspace) {
      this._reloadSkills(this.workspace)
    }
  }

  /**
   * Set the selected user.
   */
  @action.bound
  setSelectedUser(user: SearchableUser | null) {
    this.selectedUser = user
  }

  /**
   * Set the list of available skills.
   */
  @action.bound
  setAvailableSkills(skills: Skill[]) {
    this.availableSkills = skills
  }

  /**
   * Set the workspace field.
   */
  @action.bound
  setWorkspace(workspace?: Workspace | null) {
    const changed = workspace?.id !== this.workspace?.id
    this.workspace = workspace ?? null
    if (workspace && changed) {
      this._reloadSkills(workspace)
    }
  }

  /**
   * Set the skills field.
   */
  @action.bound
  setSkills(skills: Skill[]) {
    this.skills = skills
  }

  /**
   * Clears selections when the workspace is changed.
   */
  @action.bound
  clearSelections() {
    this.setSkills([])
  }

  /**
   * Preselects the workspace chosen on the page, if any.
   */
  @action.bound
  private _initialize() {
    if (this.selectedWorkspace !== null) {
      this.setWorkspace(this.selectedWorkspace)
    }
  }

  /**
   * Opens the dialog with the contextual user.
   */
  @action.bound
  showWithUser(user: SearchableUser) {
    this._initialize()
    this.setSelectedUser(user)
    this.modal.show()
  }

  /**
   * Opens the dialog with all the selected users.
   */
  @action.bound
  showWithSelections() {
    if (this.selectedUsers.length < 1) {
      this._newAlert()
        .withState('neutral')
        .saying(
          'No users selected.',
          'You must select at least one user for whom to manage skills.',
        )
        .autoDismiss(2500)
        .show()
      return
    }
    this._initialize()
    this.modal.show()
  }

  /**
   * Clears manage skills dialog state.
   */
  @action.bound
  reset() {
    this.setSelectedTab('Add')
    this.setSelectedUser(null)
    this.setAvailableSkills([])
    this.setWorkspace(null)
    this.setSkills([])
  }

  /**
   * Update the selected user(s) by adding/removing the selected skills.
   */
  submit = task.resolved(async () => {
    const workspace = this.workspace
    if (!this.validation.reset().validate().isValid || !workspace) {
      return
    }

    const users = this.selectedUser ? [this.selectedUser] : this.selectedUsers
    const operation = (
      workspace: Workspace,
      userPublicId: Id,
      skills: Skill[],
    ) =>
      this.selectedTab === 'Add'
        ? this._addMemberSkills(workspace, userPublicId, skills)
        : this._removeMemberSkills(workspace, userPublicId, skills)

    const results: SkillManagementResult[] = await Promise.all(
      users.map((user) =>
        operation(workspace, user.publicId, this.skills)
          .then(() => {
            return { user, success: true }
          })
          .catch((error: Error) => {
            return { user, error }
          }),
      ),
    )

    const successful = results.filter((r) => r.success)
    const failed = results.filter((r) => r.error)

    if (failed.length < 1) {
      this._newAlert()
        .withState('success')
        .saying(
          'Success!',
          `Skills updated for ${successful.length} ${pluralize('user', successful.length)}.`,
        )
        .autoDismiss(2500)
        .show()
      this.close()
      return
    }

    // If any skill updating failed, deselect successfully updated users.
    this._newAlert()
      .withState('error')
      .saying(
        `Failed to update skills for ${failed.length} ${pluralize('user', failed.length)}.`,
      )
      .autoDismiss(2500)
      .show()
    successful.forEach((r) => this.untoggleUser(r.user.publicId))
  })

  /**
   * Close the dialog and reset selections to default.
   */
  @action.bound
  close() {
    this.reset()
    this.modal.close()
  }

  private async _fetchSkills(workspace: Workspace) {
    const withRetry = this._createAlertRetryWrapper('Loading skills failed')
    await withRetry(() => this._listSkills({ workspaceId: workspace.id }))
    if (this.workspace === workspace) {
      runInAction(() => {
        this.availableSkills = this._getWorkspaceSkills(workspace.id)
      })
    }
  }

  @action.bound
  private _reloadSkills(workspace: Workspace) {
    this.clearSelections()
    // The retry alert has reported the failure.
    this._fetchSkills(workspace).catch(() => undefined)
  }
}
